import React, { useRef, useState } from 'react'
import {
    Input,
    Button,
    Checkbox,
    FormItem,
    FormContainer,
    Alert,
} from 'components/ui'
import { PasswordInput, ActionLink } from 'components/shared'
import { Dialog } from 'components/ui'
import useTimeOutMessage from 'utils/hooks/useTimeOutMessage'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import useAuth from 'utils/hooks/useAuth'
import ReCAPTCHA from 'react-google-recaptcha'
import { useDispatch } from 'react-redux'
import {
    setAuthPanelLoginExpand,
    setAuthPanelResetPasswordExpand,
    setAuthPanelSignupExpand,
} from 'store/theme/themeSlice'

const validationSchema = Yup.object().shape({
    email: Yup.string()
        .email('Invalid email')
        .required('Please enter your email'),
    password: Yup.string().required('Please enter your password'),
    rememberMe: Yup.bool(),
})

const SignInForm = (props) => {
    const {
        disableSubmit = false,
        className,
        forgotPasswordUrl = '/forgot-password',
        signUpUrl = '/sign-up',
    } = props
    const dispatch = useDispatch()

    const recaptchaRef = useRef(null)

    const [message, setMessage] = useTimeOutMessage(10000)
    const [loading, setLoading] = useState(false)

    const { signIn } = useAuth()
    const [dialogIsOpen, setIsOpen] = useState(false)

    const confirmSwitch = () => {
        setIsOpen(true)
    }

    const onDialogClose = (e) => {
        setIsOpen(false)
    }

    const onDialogOk = (e) => {
        setLoading(true)
        window.location.href =
            'https://marketplace.disco.info/searchArticles/index'
    }

    const onSignIn = async (values, setSubmitting) => {
        const { email, password } = values
        setSubmitting(true)
        const recaptchaToken = await recaptchaRef.current.executeAsync()

        const response = await signIn({ email, password, recaptchaToken })
        if (response.status === 'failed') {
            setMessage(response.message)
        }
        setSubmitting(false)
    }

    return (
        <div className={className}>
            {message && (
                <Alert className="mb-4 text-left" type="danger" showIcon>
                    {message}
                </Alert>
            )}
            <Formik
                // Remove this initial value
                initialValues={{
                    email: '',
                    password: '',
                    rememberMe: true,
                }}
                validationSchema={validationSchema}
                onSubmit={(values, { setSubmitting }) => {
                    if (!disableSubmit) {
                        onSignIn(values, setSubmitting)
                    } else {
                        setSubmitting(false)
                    }
                }}
            >
                {({ touched, errors, isSubmitting }) => (
                    <Form>
                        <FormContainer>
                            <FormItem
                                label={false}
                                invalid={errors.email && touched.email}
                                errorMessage={errors.email}
                                className={errors.email ? '' : 'mb-4'}
                            >
                                <Field
                                    type="text"
                                    autoComplete="off"
                                    name="email"
                                    placeholder="Email address"
                                    component={Input}
                                />
                            </FormItem>
                            <FormItem
                                label={false}
                                invalid={errors.password && touched.password}
                                errorMessage={errors.password}
                                className={errors.password ? '' : 'mb-4'}
                            >
                                <Field
                                    autoComplete="off"
                                    name="password"
                                    placeholder="Password"
                                    component={PasswordInput}
                                />
                            </FormItem>
                            <div className="flex justify-between mb-6">
                                <Field
                                    className="mb-0"
                                    name="rememberMe"
                                    component={Checkbox}
                                    children="Remember Me"
                                />
                                <span
                                    className="text-primary cursor-pointer"
                                    onClick={() => {
                                        dispatch(setAuthPanelLoginExpand(false))
                                        dispatch(
                                            setAuthPanelSignupExpand(false)
                                        )
                                        dispatch(
                                            setAuthPanelResetPasswordExpand(
                                                true
                                            )
                                        )
                                    }}
                                >
                                    Forgot Password?
                                </span>
                            </div>
                            <div className="hidden">
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    size="invisible"
                                    sitekey="6LfHNskUAAAAALXWeqW6kUjlmcU_r6zHHTHXLRay"
                                />
                            </div>
                            <Button
                                block
                                loading={isSubmitting}
                                variant="solid"
                                type="submit"
                            >
                                {isSubmitting ? 'Signing in...' : 'Sign In'}
                            </Button>
                            <div className="mt-4 text-left">
                                <span>Don't have an account yet? </span>
                                <span
                                    className="text-primary cursor-pointer"
                                    onClick={() => {
                                        dispatch(setAuthPanelLoginExpand(false))
                                        dispatch(setAuthPanelSignupExpand(true))
                                    }}
                                >
                                    Sign Up
                                </span>
                            </div>
                        </FormContainer>
                    </Form>
                )}
            </Formik>

            <div className="mt-10">
                <Button
                    block
                    variant="solid"
                    className="!bg-gold !text-[#23262B]"
                    type="button"
                    onClick={() => confirmSwitch()}
                >
                    {'Switch to marketplace'}
                </Button>
                <Dialog
                    isOpen={dialogIsOpen}
                    onClose={onDialogClose}
                    onRequestClose={onDialogClose}
                    closable={false}
                    width={'350px'}
                    overlayClassName="z-50"
                >
                    <div className="flex flex-col gap-5 justify-center items-start">
                        <img
                            className="w-12/12"
                            src="/img/logo/disco-marketplace.svg"
                            alt="Disco Marketplace"
                        />
                        <p className="text-base leading-7 text-[#23262B]">
                            Are you sure you want to switch to marketplace?
                        </p>
                        <div className="flex justify-between items-center w-full">
                            <Button
                                loading={loading}
                                block
                                variant="solid"
                                onClick={onDialogOk}
                            >
                                Yes
                            </Button>
                            <Button
                                block
                                className="text-primary"
                                variant="plain"
                                onClick={onDialogClose}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </Dialog>
            </div>
        </div>
    )
}

export default SignInForm
